import { useState } from "react";
import { useAppSelector } from "../../../../app/hooks";
import { useScrollToSection } from "../../../../app/useScrollToSection";
import RtkqResultViewer from "../../../../components/RtkqResultViewer";
import { Loading } from "../../../../components/Loading";
import { SelectDevelopmentView } from "../../../../components/input/SelectDevelopmentView";
import { DownloadButton } from "../../shared/DownloadButton";
import { EssayTraitScoreChart } from "./EssayTraitScoreChart";
import { CrTraitScoreChart } from "./CrTraitScoreChart";
import { GenreScoreChart } from "./GenreScoreChart";
import { EssayTraitScoreTable } from "./EssayTraitScoreTable";
import { CrTraitScoreTable } from "./CrTraitScoreTable";
import { GenreScoreTable } from "./GenreScoreTable";
import { useCoursePerformanceReportQuery } from "./coursePerformanceReportApiSlice";

export const CLASS_TRAIT_AND_TOTAL_SCORES = "trait-and-total-scores";
export const CLASS_WRITING_GENRE_SCORES = "writing-genre-scores";
export const CLASS_CR_SCORES = "constructed-response-scores";

export default function CoursePerformanceReport() {
	const { aspUrl, config } = useAppSelector((state) => state.site);
	useScrollToSection();

	const [view, setView] = useState("react");

	const result = useCoursePerformanceReportQuery({});
	const data = result.data;

	return (
		<section>
			<div className="flex justify-between">
				<div className="w-3/4">
					<h1>Class Performance Report</h1>
				</div>
				<SelectDevelopmentView
					value={view}
					setValue={(v) => setView(v as string)}
					simpleListBoxId="viewFilter"
				/>
			</div>
			{view == "react" && (
				<>
					<section id={CLASS_TRAIT_AND_TOTAL_SCORES} className="mt-8">
						<h2>
							{config?.isUtahComposeSite ? "Analytical" : "Trait"} and Total Scores
						</h2>
						<p className="mt-4">
							The report below displays the average score for each writing{" "}
							{config?.isUtahComposeSite ? "element" : "trait"}, total score, and
							teacher-scored textual evidence and content accuracy categories by class
							and by student. The averages are calculated using the score from the
							most recent draft of each essay. To view a detailed performance report
							for a specific student, click the student’s name.
						</p>
						<Loading loading={result.isLoading} />
						{data && (
							<>
								<div className="flex place-content-end w-full mt-4">
									<DownloadButton
										url={`${aspUrl}/Report/DownloadEssayScoresFilteredByCourse?courseId=${data.courseId}`}
										disabled={data.averageTraitScoresByWriter.length == 0}
									/>
								</div>
								<EssayTraitScoreTable
									className="mt-4"
									courseId={data.courseId}
									writerData={data.averageTraitScoresByWriter}
									classData={data.averageTraitScoresForCourse}
								/>
								<EssayTraitScoreChart
									className="mt-8 p-4 border-2 border-gray-200"
									courseId={data.courseId}
									writerData={data.averageTraitScoresByWriter}
									classData={data.averageTraitScoresForCourse}
								/>
							</>
						)}
					</section>

					<section id={CLASS_CR_SCORES} className="mt-8">
						<h2>Constructed Response Scores</h2>
						<p className="mt-4">
							The report below displays the average holistic score as well as
							teacher-scored textual evidence and content accuracy categories by class
							and by student. The scores are from the most recent draft of each
							response. To view a detailed performance report for a specific student,
							click the student’s name.
						</p>
						<Loading loading={result.isLoading} />
						{data && (
							<>
								<div className="flex place-content-end w-full mt-4">
									<DownloadButton
										url={`${aspUrl}/Report/DownloadConstructedResponseEssayScoresFilteredByCourse?courseId=${data.courseId}`}
										disabled={
											data.averageConstructedResponseScoresByWriter.length ==
											0
										}
									/>
								</div>
								<CrTraitScoreTable
									className="mt-4"
									courseId={data.courseId}
									writerData={data.averageConstructedResponseScoresByWriter}
									classData={data.averageConstructedResponseScoresForCourse}
								/>
								<CrTraitScoreChart
									className="mt-8 p-4 border-2 border-gray-200"
									courseId={data.courseId}
									writerData={data.averageConstructedResponseScoresByWriter}
									classData={data.averageConstructedResponseScoresForCourse}
								/>
							</>
						)}
					</section>

					<section id={CLASS_WRITING_GENRE_SCORES} className="mt-8">
						<h2>Writing Genre Scores</h2>
						<p className="mt-4">
							The report below compares the average score on essays designated to a
							specific genre of writing by class and each student. Click on a
							student’s name to view individual averages by{" "}
							{config?.isUtahComposeSite ? "writing element" : "trait"} and total
							score.
						</p>
						<Loading loading={result.isLoading} />
						{data && (
							<>
								<div className="flex place-content-end w-full mt-4">
									<DownloadButton
										url={`${aspUrl}/Report/DownloadStandardScoresFilteredByCourse?courseId=${data.courseId}`}
										disabled={data.averageStandardScoresByWriter.length == 0}
									/>
								</div>
								<GenreScoreTable
									className="mt-4"
									courseId={data.courseId}
									writerData={data.averageStandardScoresByWriter}
									classData={data.averageStandardScoresForCourse}
								/>
								<GenreScoreChart
									className="mt-8 p-4 border-2 border-gray-200"
									courseId={data.courseId}
									writerData={data.averageStandardScoresByWriter}
									classData={data.averageStandardScoresForCourse}
								/>
							</>
						)}
					</section>
				</>
			)}
			{view == "raw" && (
				<RtkqResultViewer heading="Performance Report Data" result={result} />
			)}
		</section>
	);
}

export function studentPerformanceLink(courseId?: number, writerId?: number, section?: string) {
	let link = "/reports/student/performance?";
	if (courseId) link += `courseId=${courseId}&`;
	if (writerId) link += `writerId=${writerId}&`;
	if (section) link += `section=${section}&`;
	return link.slice(0, -1);
}
